import { FC } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

interface IDeleteReceiptsDialogProps {
  open: boolean;
  invoiceNumber?: string;
  /** Voucher name used in the copy, e.g. "Invoice". */
  moduleLabel?: string;
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/** Confirmation shown before deleting every payment receipt applied to an invoice. */
const DeleteReceiptsDialog: FC<IDeleteReceiptsDialogProps> = ({ open, invoiceNumber, moduleLabel = 'Invoice', deleting, onClose, onConfirm }) => (
  <Dialog
    open={open}
    onClose={() => !deleting && onClose()}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: 3,
        boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
      }
    }}
    sx={{
      '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
      },
    }}
  >
    <DialogTitle
      sx={{
        backgroundColor: '#fef2f2',
        borderBottom: '1px solid #fecaca',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 3,
        px: 3
      }}
    >
      <Box sx={{
        backgroundColor: '#fee2e2',
        borderRadius: '50%',
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <DeleteIcon sx={{ color: '#dc2626', fontSize: 28 }} />
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1.25rem', lineHeight: 1.2 }}>
          Delete All Receipts
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5, fontSize: '0.875rem' }}>
          This action cannot be undone
        </Typography>
      </Box>
    </DialogTitle>

    <DialogContent sx={{ py: 4, px: 3 }}>
      <Typography variant="body1" sx={{ color: '#1f2937', fontSize: '1.125rem', fontWeight: 400, lineHeight: 1.7, pt: 2, pb: 1, mb: 3 }}>
        Are you sure you want to delete all payment receipts for <strong>{invoiceNumber}</strong>?
      </Typography>
      <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.95rem', lineHeight: 1.6, mb: 3, fontWeight: 600 }}>
        This Will Permanently Remove All Payment Receipts And Associated Journal Entries For This {moduleLabel}.
      </Typography>

      <Box sx={{
        backgroundColor: '#fef3c7',
        border: '1px solid #f59e0b',
        borderRadius: 2,
        p: 3,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2
      }}>
        <Box sx={{
          backgroundColor: '#fbbf24',
          borderRadius: '50%',
          p: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 24,
          height: 24
        }}>
          <Typography sx={{ color: '#ffffff', fontSize: '0.875rem', fontWeight: 'bold' }}>!</Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#92400e', fontSize: '0.875rem', mb: 0.5 }}>
            Important Warning
          </Typography>
          <Typography variant="body2" sx={{ color: '#92400e', fontSize: '0.875rem', lineHeight: 1.5 }}>
            All payment receipts linked to this {moduleLabel.toLowerCase()} will be removed. This data cannot be recovered once deleted.
          </Typography>
        </Box>
      </Box>
    </DialogContent>

    <DialogActions sx={{
      backgroundColor: '#f9fafb',
      borderTop: '1px solid #e5e7eb',
      px: 3,
      py: 2.5,
      gap: 1.5
    }}>
      <Button
        onClick={onClose}
        disabled={deleting}
        variant="outlined"
        sx={{
          minWidth: 100,
          height: 40,
          textTransform: 'none',
          fontWeight: 500,
          borderColor: '#d1d5db',
          color: '#374151',
          '&:hover': { borderColor: '#6b7280', backgroundColor: '#e5e7eb', color: '#1f2937' }
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        disabled={deleting}
        startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon sx={{ fontSize: 18 }} />}
        sx={{
          minWidth: 140,
          height: 40,
          textTransform: 'none',
          fontWeight: 600,
          backgroundColor: '#dc2626',
          '&:hover': { backgroundColor: '#b91c1c' },
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
        }}
      >
        {deleting ? 'Deleting...' : 'Delete Receipts'}
      </Button>
    </DialogActions>
  </Dialog>
);

export default DeleteReceiptsDialog;
